/**
 * Admin Content Management
 * Organized with tabs: B2B / Privat / Stillas Job / Banner Settings
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Megaphone, 
  Save, 
  Calendar, 
  Palette,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Home,
  Building2,
  ImageIcon,
  Trash2,
  Upload,
  Camera,
  Plus,
  GripVertical,
  ArrowLeftRight,
  Briefcase,
  Settings
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Font size options
const TITLE_SIZES = [
  { value: "12px", label: "XS (12px)" },
  { value: "14px", label: "S (14px)" },
  { value: "16px", label: "M (16px)" },
  { value: "18px", label: "L (18px)" },
  { value: "20px", label: "XL (20px)" },
];

const DESC_SIZES = [
  { value: "10px", label: "Mini (10px)" },
  { value: "12px", label: "XS (12px)" },
  { value: "14px", label: "S (14px)" },
  { value: "16px", label: "M (16px)" },
];

const FONT_FAMILIES = [
  { value: "default", label: "Default (System)" },
  { value: "unbounded", label: "Unbounded (Bold)" },
  { value: "mono", label: "Monospace (Code)" },
];

// ============================================
// IMAGE GALLERY COMPONENT (Reusable)
// ============================================
function ImageGalleryManager({ 
  pageType, 
  title, 
  description, 
  icon: Icon,
  previewUrl 
}) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  // Fetch images for this page type
  useEffect(() => {
    fetchImages();
  }, [pageType]);

  const fetchImages = async () => {
    const token = localStorage.getItem("scaffoldx_token");
    try {
      const res = await fetch(`${API}/api/banner/project-images/${pageType}/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setImages(data.images || []);
      }
    } catch (error) {
      console.error("Failed to fetch images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem("scaffoldx_token");

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} er for stor (maks 10MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await fetch(`${API}/api/projects-v3/public-upload`, {
          method: 'POST',
          body: formData
        });

        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          // Add to page-specific gallery
          const addRes = await fetch(
            `${API}/api/banner/project-images/${pageType}/admin/add?image_url=${encodeURIComponent(uploadData.secure_url)}`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          if (addRes.ok) {
            setImages(prev => [...prev, uploadData.secure_url]);
            toast.success(`Bilde lastet opp!`);
          }
        } else {
          toast.error(`Kunne ikke laste opp ${file.name}`);
        }
      } catch (error) {
        toast.error(`Feil ved opplasting`);
      }
    }

    setUploading(false);
    e.target.value = '';
  };

  const deleteImage = async (index) => {
    const token = localStorage.getItem("scaffoldx_token");
    try {
      const res = await fetch(`${API}/api/banner/project-images/${pageType}/admin/${index}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setImages(prev => prev.filter((_, i) => i !== index));
        toast.success("Bilde slettet!");
      }
    } catch (error) {
      toast.error("Sletting feilet");
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { e.target.style.opacity = '0.5'; }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setImages(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Save to backend
    setIsReordering(true);
    const token = localStorage.getItem("scaffoldx_token");
    try {
      await fetch(`${API}/api/banner/project-images/${pageType}/admin/reorder`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ images: newImages })
      });
      toast.success("Rekkefølge oppdatert!");
    } catch (error) {
      toast.error("Kunne ikke lagre rekkefølgen");
      fetchImages();
    } finally {
      setIsReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#F7931A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Icon className="w-6 h-6 text-[#F7931A]" />
            {title}
            <Badge className="bg-[#F7931A]/20 text-[#F7931A] ml-2">
              {images.length} bilder
            </Badge>
            {isReordering && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-[#23F7DD] rounded-full animate-spin ml-2" />
            )}
          </h3>
          <p className="text-[#94A3B8] text-sm mt-1">{description}</p>
        </div>
        {previewUrl && (
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
              <ExternalLink className="w-4 h-4 mr-2" />
              Se side
            </Button>
          </a>
        )}
      </div>

      {/* Upload area */}
      <label className="flex items-center justify-center gap-3 p-8 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-[#F7931A]/50 hover:bg-[#F7931A]/5 transition-all">
        {uploading ? (
          <>
            <div className="w-6 h-6 border-2 border-white/30 border-t-[#F7931A] rounded-full animate-spin" />
            <span className="text-[#94A3B8]">Laster opp...</span>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 text-[#F7931A]" />
            <div className="text-center">
              <span className="text-white font-medium block">Klikk for å laste opp bilder</span>
              <span className="text-[#64748B] text-sm">PNG, JPG, WebP eller PDF (maks 10MB)</span>
            </div>
          </>
        )}
        <input
          type="file"
          multiple
          accept="image/*,.pdf,application/pdf"
          onChange={handleImageUpload}
          className="hidden"
          disabled={uploading}
        />
      </label>

      {/* Drag hint */}
      {images.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-[#64748B] justify-center">
          <ArrowLeftRight className="w-4 h-4" />
          <span>Dra og slipp bildene for å endre rekkefølgen</span>
        </div>
      )}

      {/* Images grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {images.map((img, index) => (
            <div 
              key={`${img}-${index}`} 
              className={`relative group aspect-square transition-all duration-200 ${
                draggedIndex === index ? 'opacity-50 scale-95' : ''
              } ${
                dragOverIndex === index ? 'ring-2 ring-[#23F7DD] ring-offset-2 ring-offset-[#0A0A0A] scale-105' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={() => setDragOverIndex(null)}
              onDrop={(e) => handleDrop(e, index)}
            >
              {img.includes('/raw/upload/') ? (
                <div className="w-full h-full bg-white/10 rounded-xl flex flex-col items-center justify-center border border-white/10">
                  <ImageIcon className="w-10 h-10 text-red-400" />
                  <span className="text-xs text-[#94A3B8] mt-1">PDF</span>
                </div>
              ) : (
                <img 
                  src={img} 
                  alt={`Bilde ${index + 1}`}
                  className="w-full h-full object-cover rounded-xl cursor-grab active:cursor-grabbing"
                  draggable={false}
                />
              )}
              
              {/* Drag handle */}
              <div className="absolute top-2 right-2 bg-black/70 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical className="w-4 h-4" />
              </div>
              
              {/* Delete overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                <Button
                  size="sm"
                  variant="destructive"
                  className="bg-red-500 hover:bg-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteImage(index);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Position badge */}
              <div className="absolute bottom-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded-lg font-medium">
                #{index + 1}
              </div>
            </div>
          ))}
          
          {/* Add more placeholder */}
          <label className="aspect-square border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center cursor-pointer hover:border-[#F7931A]/50 hover:bg-[#F7931A]/5 transition-all">
            <div className="text-center">
              <Plus className="w-8 h-8 text-[#64748B] mx-auto mb-1" />
              <span className="text-[#64748B] text-xs">Legg til</span>
            </div>
            <input
              type="file"
              accept="image/*,.pdf,application/pdf"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
      ) : (
        <div className="text-center py-12 text-[#64748B]">
          <Camera className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>Ingen bilder ennå</p>
          <p className="text-sm">Last opp bilder for å vise dem på {title.toLowerCase()}</p>
        </div>
      )}
    </div>
  );
}


// ============================================
// BANNER SETTINGS COMPONENT
// ============================================
function BannerSettings() {
  const [banner, setBanner] = useState({
    enabled: false,
    title_no: "",
    title_en: "",
    description_no: "",
    description_en: "",
    discount_text: "",
    expires_at: "",
    background_color: "#F7931A",
    text_color: "#000000",
    title_size: "16px",
    desc_size: "14px",
    font_family: "default",
    pages: ["privat", "b2b"]
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    const token = localStorage.getItem("scaffoldx_token");
    try {
      const res = await fetch(`${API}/api/banner/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.banner) {
          setBanner(prev => ({ ...prev, ...data.banner }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch banner:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveBanner = async () => {
    setSaving(true);
    const token = localStorage.getItem("scaffoldx_token");
    try {
      const res = await fetch(`${API}/api/banner/admin`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(banner)
      });
      if (res.ok) {
        toast.success("Banner lagret!");
      } else {
        toast.error("Kunne ikke lagre banner");
      }
    } catch (error) {
      toast.error("Lagring feilet");
    } finally {
      setSaving(false);
    }
  };

  const togglePage = (page) => {
    setBanner(prev => ({
      ...prev,
      pages: prev.pages.includes(page) 
        ? prev.pages.filter(p => p !== page)
        : [...prev.pages, page]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#F7931A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Toggle */}
      <Card className={`${banner.enabled ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {banner.enabled ? (
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-400" />
              )}
              <div>
                <p className="font-medium text-white">
                  {banner.enabled ? "Banner Aktiv" : "Banner Inaktiv"}
                </p>
                <p className="text-sm text-[#94A3B8]">
                  {banner.enabled ? "Vises på valgte sider" : "Banner-ul este dezactivat"}
                </p>
              </div>
            </div>
            <Switch 
              checked={banner.enabled}
              onCheckedChange={(checked) => setBanner(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Norwegian Text */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              🇳🇴 Norsk Tekst
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#94A3B8]">Tittel</Label>
              <Input
                value={banner.title_no}
                onChange={(e) => setBanner(prev => ({ ...prev, title_no: e.target.value }))}
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="F.eks: Vårkampanje!"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Beskrivelse</Label>
              <Textarea
                value={banner.description_no}
                onChange={(e) => setBanner(prev => ({ ...prev, description_no: e.target.value }))}
                className="bg-white/5 border-white/20 text-white mt-1"
                rows={3}
                placeholder="F.eks: 20% rabatt på alle stillastjenester"
              />
            </div>
          </CardContent>
        </Card>

        {/* English Text */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              🇬🇧 English Text
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#94A3B8]">Title</Label>
              <Input
                value={banner.title_en}
                onChange={(e) => setBanner(prev => ({ ...prev, title_en: e.target.value }))}
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="E.g.: Spring Campaign!"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Description</Label>
              <Textarea
                value={banner.description_en}
                onChange={(e) => setBanner(prev => ({ ...prev, description_en: e.target.value }))}
                className="bg-white/5 border-white/20 text-white mt-1"
                rows={3}
                placeholder="E.g.: 20% off all scaffolding services"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Innstillinger
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-[#94A3B8]">Rabatt-tekst (valgfritt)</Label>
              <Input
                value={banner.discount_text}
                onChange={(e) => setBanner(prev => ({ ...prev, discount_text: e.target.value }))}
                className="bg-white/5 border-white/20 text-white mt-1"
                placeholder="F.eks: 20%"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8] flex items-center gap-1">
                <Clock className="w-4 h-4" /> Utløpsdato
              </Label>
              <Input
                type="date"
                value={banner.expires_at?.split('T')[0] || ''}
                onChange={(e) => setBanner(prev => ({ 
                  ...prev, 
                  expires_at: new Date(e.target.value).toISOString() 
                }))}
                className="bg-white/5 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Font</Label>
              <select
                value={banner.font_family}
                onChange={(e) => setBanner(prev => ({ ...prev, font_family: e.target.value }))}
                className="w-full bg-white/5 border border-white/20 text-white rounded-md px-3 py-2 mt-1"
              >
                {FONT_FAMILIES.map(f => (
                  <option key={f.value} value={f.value} className="bg-[#1A1A1A]">{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#94A3B8] flex items-center gap-1">
                <Palette className="w-4 h-4" /> Bakgrunnsfarge
              </Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={banner.background_color}
                  onChange={(e) => setBanner(prev => ({ ...prev, background_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={banner.background_color}
                  onChange={(e) => setBanner(prev => ({ ...prev, background_color: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white flex-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8] flex items-center gap-1">
                <Palette className="w-4 h-4" /> Tekstfarge
              </Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={banner.text_color}
                  onChange={(e) => setBanner(prev => ({ ...prev, text_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={banner.text_color}
                  onChange={(e) => setBanner(prev => ({ ...prev, text_color: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white flex-1"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[#94A3B8]">Vis på sider</Label>
            <div className="flex gap-3 mt-2">
              <Button
                type="button"
                variant={banner.pages.includes("privat") ? "default" : "outline"}
                size="sm"
                onClick={() => togglePage("privat")}
                className={banner.pages.includes("privat") 
                  ? "bg-[#23F7DD] text-black hover:bg-[#1AD4BD]" 
                  : "border-white/20 text-white hover:bg-white/10"}
              >
                <Home className="w-4 h-4 mr-1" />
                Privat Oslo
              </Button>
              <Button
                type="button"
                variant={banner.pages.includes("b2b") ? "default" : "outline"}
                size="sm"
                onClick={() => togglePage("b2b")}
                className={banner.pages.includes("b2b") 
                  ? "bg-[#23F7DD] text-black hover:bg-[#1AD4BD]" 
                  : "border-white/20 text-white hover:bg-white/10"}
              >
                <Building2 className="w-4 h-4 mr-1" />
                B2B Oslo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {banner.enabled && banner.title_no && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Forhåndsvisning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="p-4 rounded-lg text-center"
              style={{ 
                backgroundColor: banner.background_color,
                color: banner.text_color,
                fontFamily: banner.font_family === 'unbounded' ? "'Unbounded', sans-serif" : 
                           banner.font_family === 'mono' ? 'monospace' : 'inherit'
              }}
            >
              <p style={{ fontSize: banner.title_size }} className="font-bold">
                {banner.discount_text && <span className="mr-2">{banner.discount_text}</span>}
                {banner.title_no}
              </p>
              <p style={{ fontSize: banner.desc_size }} className="opacity-90 mt-1">
                {banner.description_no}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveBanner}
          disabled={saving}
          className="bg-[#F7931A] hover:bg-[#E8820F] text-white px-8"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              Lagrer...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Lagre Banner
            </>
          )}
        </Button>
      </div>
    </div>
  );
}


// ============================================
// MAIN PAGE COMPONENT
// ============================================
export default function AdminBanner() {
  const [activeTab, setActiveTab] = useState("home");

  return (
    <>
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
                <Megaphone className="w-8 h-8 text-[#F7931A]" />
                Innholdsstyring
              </h1>
              <p className="text-[#94A3B8] mt-1">
                Administrer bilder og banner for offentlige sider
              </p>
            </div>
            
            {/* Quick links */}
            <div className="hidden sm:flex gap-2">
              <a href="/stillas-privat-oslo" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  <Home className="w-4 h-4 mr-2" />
                  Privat
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
              <a href="/stillasfirma-oslo" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                  <Building2 className="w-4 h-4 mr-2" />
                  B2B
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white/5 border border-white/10 p-1 mb-6 w-full sm:w-auto">
              <TabsTrigger 
                value="home" 
                className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black px-6"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </TabsTrigger>
              <TabsTrigger 
                value="b2b" 
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black px-6"
              >
                <Building2 className="w-4 h-4 mr-2" />
                B2B Bedrift
              </TabsTrigger>
              <TabsTrigger 
                value="privat"
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black px-6"
              >
                <Home className="w-4 h-4 mr-2" />
                Privat
              </TabsTrigger>
              <TabsTrigger 
                value="stillasjob"
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black px-6"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Stillas Job
              </TabsTrigger>
              <TabsTrigger 
                value="banner"
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black px-6"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Banner
              </TabsTrigger>
            </TabsList>

            {/* Home Tab */}
            <TabsContent value="home" className="mt-0">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <ImageGalleryManager
                    pageType="home"
                    title="Forsidebilder"
                    description="Bilder som vises i 'Våre Prosjekter' seksjonen på forsiden (landing page)"
                    icon={Home}
                    previewUrl="/"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* B2B Tab */}
            <TabsContent value="b2b" className="mt-0">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <ImageGalleryManager
                    pageType="b2b"
                    title="B2B Prosjektbilder"
                    description="Bilder som vises i 'Tidligere prosjekter' seksjonen på B2B-siden (stillasfirma-oslo)"
                    icon={Building2}
                    previewUrl="/stillasfirma-oslo"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Privat Tab */}
            <TabsContent value="privat" className="mt-0">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <ImageGalleryManager
                    pageType="privat"
                    title="Privat Prosjektbilder"
                    description="Bilder som vises i 'Tidligere prosjekter' seksjonen på Privat-siden (stillas-privat-oslo)"
                    icon={Home}
                    previewUrl="/stillas-privat-oslo"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Stillas Job Tab */}
            <TabsContent value="stillasjob" className="mt-0">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="text-center py-16">
                    <Briefcase className="w-20 h-20 text-[#64748B] mx-auto mb-4 opacity-30" />
                    <h3 className="text-xl font-bold text-white mb-2">Stillas Job</h3>
                    <p className="text-[#94A3B8] max-w-md mx-auto">
                      Denne seksjonen kommer snart. Her kan du administrere innhold for Stillas Job-siden.
                    </p>
                    <Badge className="mt-4 bg-[#F7931A]/20 text-[#F7931A]">
                      Kommer snart
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Banner Tab */}
            <TabsContent value="banner" className="mt-0">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Megaphone className="w-6 h-6 text-[#F7931A]" />
                    <div>
                      <h3 className="text-xl font-bold text-white">Kampanjebanner</h3>
                      <p className="text-[#94A3B8] text-sm">
                        Sett opp tidsbegrensede tilbud som vises øverst på sidene
                      </p>
                    </div>
                  </div>
                  <BannerSettings />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/10 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-[#64748B]">
          <span>AllStillas Admin Panel</span>
          <Link to="/admin" className="hover:text-white transition-colors">
            ← Tilbake til admin
          </Link>
        </div>
      </footer>
    </>
  );
}
